import { ReactNode, memo } from 'react';
import { cn } from '@/lib/utils';

export interface AvatarProps {
  /** Imagen del avatar (URL o elemento) */
  src?: string;
  /** Texto alternativo si no hay imagen */
  alt?: string;
  /** Iniciales a mostrar si no hay imagen */
  initials?: string;
  /** Tamaño del avatar */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Clase CSS adicional */
  className?: string;
  /** Contenido personalizado (ícono, etc.) */
  children?: ReactNode;
  /** Si está online/activo */
  online?: boolean;
  /** Si está offline */
  offline?: boolean;
  /** Si es cuadrado (rounded-sm en lugar de rounded-full) */
  square?: boolean;
}

/**
 * Avatar - Foto de perfil con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Ring inset sutil para definir bordes
 * - Dark mode: ring-white/10
 * - Uso de size-* en lugar de w-* h-*
 */
const sizeClasses = {
  xs: 'size-6 text-xs',
  sm: 'size-8 text-sm',
  md: 'size-10 text-base',
  lg: 'size-12 text-lg',
  xl: 'size-16 text-xl',
};

const statusSizes = {
  xs: 'size-2',
  sm: 'size-2.5',
  md: 'size-3',
  lg: 'size-3.5',
  xl: 'size-4',
};

export const Avatar = memo(function Avatar({
  src,
  alt,
  initials,
  size = 'md',
  className,
  children,
  online,
  offline,
  square = false,
}: AvatarProps) {
  const sizeClass = sizeClasses[size];
  const statusSize = statusSizes[size];
  const roundedClass = square ? 'rounded-sm' : 'rounded-full';
  
  const content = children || (
    <>
      {src ? (
        <img
          src={src}
          alt={alt || 'Avatar'}
          className={cn(
            'size-full object-cover',
            roundedClass,
            // Ring inset sutil para definir bordes
            'ring-1 ring-inset ring-black/5 dark:ring-white/10'
          )}
        />
      ) : initials ? (
        <span className={cn(
          'flex size-full items-center justify-center font-medium text-white',
          roundedClass,
          'bg-primary-600 dark:bg-primary-500',
          // Ring inset sutil
          'ring-1 ring-inset ring-black/5 dark:ring-white/10'
        )}>
          {initials.slice(0, 2).toUpperCase()}
        </span>
      ) : (
        <div className={cn(
          'size-full flex items-center justify-center',
          roundedClass,
          'bg-secondary-100 dark:bg-secondary-800',
          // Ring inset sutil
          'ring-1 ring-inset ring-black/5 dark:ring-white/10'
        )}>
          <svg 
            className="size-1/2 text-secondary-300 dark:text-secondary-500" 
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </>
  );

  const statusIndicator = (online || offline) && (
    <span
      className={cn(
        'absolute bottom-0 right-0 rounded-full',
        'border-2 border-white dark:border-secondary-800',
        statusSize,
        online ? 'bg-success-500' : 'bg-secondary-400'
      )}
    />
  );

  return (
    <div className={cn('relative inline-flex', sizeClass, className)}>
      {content}
      {statusIndicator}
    </div>
  );
});
